import { UsageService } from '../../../../../core/usage/usage.service';
import { Component, Inject, OnInit, ChangeDetectorRef, AfterViewInit, inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, Observable, startWith, firstValueFrom, debounce, debounceTime } from 'rxjs';
import { GroupService } from 'src/app/core/services/group.service';
import { PatientFinancalBillingService } from 'src/app/core/services/patient-financal-billing.service';
import { PatientFinancialService } from 'src/app/core/services/patient-financial.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { FileDownloadService } from 'src/app/core/services/file-download.service';
import { resolveVatRate } from 'src/app/core/utils/vat.util';

/**
 * Capture Payment Component
 * 
 * IMPORTANT: Server calls are temporarily commented out for PDF generation testing.
 * The actual service calls to fetch unpaid invoices and save payment receipts will be restored later.
 * DO NOT REMOVE the commented code - it indicates where the service integration will be restored.
 */
@Component({
  selector: 'app-capture-payment',
  templateUrl: './capture-payment.component.html',
  styleUrls: ['./capture-payment.component.scss']
})
export class CapturePaymentComponent implements OnInit, AfterViewInit {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  form!: FormGroup;
  unpaidInvoices: any[] = [];
  invoiceId: string = '';
  outstandingBalance: number = 0;
  remainingToAllocate: number = 0;
  dueInvoices: any[] = [];
  invoiceBalanceDue: number = 0;

  /**
   * Get appropriate description message for credit type payments
   */
  private getCreditDescription(paymentMethod: string, invoiceNumber: string): string {
    if (!paymentMethod) {
      return `Payment received for invoice ${invoiceNumber}`;
    }

    const method = paymentMethod.toLowerCase();
    
    if (method.includes('credit:')) {
      if (method.includes('pro bono')) {
        return `Pro bono credit applied for invoice ${invoiceNumber}`;
      } else if (method.includes('patient balance')) {
        return `Patient balance credit applied for invoice ${invoiceNumber}`;
      } else if (method.includes('bad debt')) {
        return `Bad debt written off for invoice ${invoiceNumber}`;
      } else if (method.includes('discounted')) {
        return `Discount credit applied for invoice ${invoiceNumber}`;
      } else if (method.includes('refund')) {
        return `Refund credit applied for invoice ${invoiceNumber}`;
      } else if (method.includes('other')) {
        return `Credit applied for invoice ${invoiceNumber}`;
      } else {
        // Generic credit
        return `Credit applied for invoice ${invoiceNumber}`;
      }
    }
    
    // Regular payment
    return `Payment received for invoice ${invoiceNumber}`;
  }

  // New properties for the new payment system
  newUnpaidInvoices: any[] = [];
  isLoadingNewInvoices: boolean = false;
  newPaymentData: any = null;

  // Add at the top of the class
  addExcessAsCredit: boolean = true;

  private patient: any;
  private group: any;

  // Cache for available invoices to prevent infinite render cycles
  private availableInvoicesCache: Map<number, any[]> = new Map();
  private lastSelectedIds: string[] = [];

  private specificInvoice: any = {};

  // UI state
  isSaving: boolean = false;
  isProcessingAvailableInvoices: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CapturePaymentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private patientBillingService: PatientFinancalBillingService,
    private patientFinancialService: PatientFinancialService,
    private changeDetectorRef: ChangeDetectorRef,
    private patientService: PatientService,
    private groupService: GroupService,
    private fileDownloadService: FileDownloadService
  ) {
  }

  ngOnInit(): void {
    this.specificInvoice = this.data?.specificInvoice;
    this.usage.flow('payment', 'start');
    console.log('Specific invoice:', this.specificInvoice);

    this.form = this.fb.group({
      paymentDate: [new Date().toISOString().split('T')[0], Validators.required],
      amountRecieved: [this.specificInvoice?.outstandingBalance || 0, [Validators.required, Validators.min(0.01)]],
      paymentMethod: ['Cash', Validators.required],
      paymentRows: this.fb.array([]),
      notes: ['']
    });

    this.invoiceId = this.data?.invoiceId;
    this.outstandingBalance = this.data?.totalOutstanding;

    this.patientService.currentPatient$.subscribe((patient) => {
      this.patient = patient;
    });
    this.groupService.selectedGroup$.subscribe((group) => {
      this.group = group;
    });

    this.patientFinancialService.billingDocuments$.subscribe({
      next: (result) => {
        // Filter for invoices with outstanding balances
        this.newUnpaidInvoices = result.filter((doc: any) =>
          doc.active && doc.outstandingBalance > 0 &&
          (doc.status === 'pending' || doc.status === 'partially_paid')
        );

        // Map to the format expected by the component
        this.unpaidInvoices = this.newUnpaidInvoices.map((doc: any) => ({
          ...doc,
          invoiceId: doc.id, // Use the billing document ID
          billingNumber: doc.billingNumber,
          balance: doc.outstandingBalance,
          totalDue: doc.totalDue,
          items: doc.lineItems || []
        }));

        this.dueInvoices = this.unpaidInvoices;

        // Handle specific invoice if provided
        console.log('🔍 DEBUG: Checking for specific invoice...');
        console.log('🔍 DEBUG: specificInvoice:', this.specificInvoice);
        console.log('🔍 DEBUG: unpaidInvoices count:', this.unpaidInvoices.length);
        console.log('🔍 DEBUG: unpaidInvoices:', this.unpaidInvoices);
        console.log('🔍 DEBUG: unpaidInvoices IDs:', this.unpaidInvoices.map(inv => ({ id: inv.id, invoiceId: inv.invoiceId, billingNumber: inv.billingNumber })));

        if (this.specificInvoice && (this.specificInvoice.invoiceId || this.specificInvoice.id)) {
          const specificInvoiceId = this.specificInvoice.invoiceId || this.specificInvoice.id;
          console.log('🔍 DEBUG: Processing specific invoice with ID:', specificInvoiceId);
          
          // Find the specific invoice in the unpaid invoices list
          const specificInvoiceData = this.unpaidInvoices.find(inv => 
            inv.invoiceId === specificInvoiceId || 
            inv.id === specificInvoiceId ||
            inv.billingNumber === this.specificInvoice.billingNumber
          );

          console.log('🔍 DEBUG: Found specific invoice data:', specificInvoiceData);
          console.log('🔍 DEBUG: Looking for invoice with ID:', specificInvoiceId);
          console.log('🔍 DEBUG: Available invoice IDs in unpaidInvoices:', this.unpaidInvoices.map(inv => inv.id));
          console.log('🔍 DEBUG: Available invoice IDs (invoiceId):', this.unpaidInvoices.map(inv => inv.invoiceId));

          if (specificInvoiceData) {
            console.log('🔍 DEBUG: Setting amount received to:', specificInvoiceData.balance || this.specificInvoice.outstandingBalance || 0);
            
            // Set the amount received to the specific invoice's outstanding balance
            this.form.patchValue({
              amountRecieved: specificInvoiceData.balance || this.specificInvoice.outstandingBalance || 0
            }, { emitEvent: false });

            console.log('🔍 DEBUG: Adding payment row for invoice:', specificInvoiceData.invoiceId);
            // Add a payment row for the specific invoice
            this.addPaymentRow(specificInvoiceData.invoiceId);
            
            console.log('🔍 DEBUG: Payment rows count after adding:', this.paymentRows.length);
            
            // Set the allocated amount to the invoice's balance
            if (this.paymentRows.length > 0) {
              const lastRow = this.paymentRows.at(this.paymentRows.length - 1);
              console.log('🔍 DEBUG: Last payment row before patching:', lastRow.value);
              
              lastRow.patchValue({
                linkedInvoice: specificInvoiceData.invoiceId,
                allocatedAmount: specificInvoiceData.balance || this.specificInvoice.outstandingBalance || 0
              });
              
              console.log('🔍 DEBUG: Last payment row after patching:', lastRow.value);
            }
          } else {
            console.log('🔍 DEBUG: Specific invoice not found in unpaid invoices list');
            console.log('🔍 DEBUG: Available invoice IDs:', this.unpaidInvoices.map(inv => ({ id: inv.id, invoiceId: inv.invoiceId, billingNumber: inv.billingNumber })));
          }
        } else {
          console.log('🔍 DEBUG: No specific invoice provided, using default behavior');
          // Add payment rows for each unpaid invoice (default behavior)
          if (this.paymentRows.length === 0) {
            this.addPaymentRow();
          }
        }

        // Patch invoice balances
        this.paymentRows.controls.forEach((control) => {
          this.patchInvoiceBalance(control);
        });

        // Clear cache since unpaid invoices have changed
        this.clearAvailableInvoicesCache();

        this.isLoadingNewInvoices = false;
      },
      error: (error) => {
        console.error('Error loading new unpaid invoices:', error);
        this.isLoadingNewInvoices = false;
      }
    });

    // Subscribe to amount received changes for auto-allocation
    this.form.get('amountRecieved')?.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.autoAllocatePayment();
      });

    // Subscribe to payment rows changes to clear cache when linkedInvoice values change
    this.paymentRows.valueChanges.subscribe(() => {
      this.clearAvailableInvoicesCache();
    });



    /*  this.form.valueChanges.subscribe(() => {
       this.updateAllocationStatus();
       this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
     }); */
  }

  ngAfterViewInit(): void {
    // The specific invoice handling is now done in the billingDocuments$ subscription
    // after the unpaid invoices are loaded, so this method can be simplified
    if (this.specificInvoice && this.specificInvoice.invoiceId) {
      // Force change detection to ensure the UI is updated
      this.changeDetectorRef.detectChanges();
    }
  }

  get paymentRows(): FormArray {
    return this.form.get('paymentRows') as FormArray;
  }

  createPaymentRow(invoiceNumber: string = ''): FormGroup {
    console.log('🔍 DEBUG: createPaymentRow called with invoiceNumber:', invoiceNumber);
    
    const paymentRow = this.fb.group({
      linkedInvoice: [invoiceNumber, Validators.required],
      allocatedAmount: [0, [Validators.required, Validators.min(0.01)]],
      balance: [0]
    });
    
    console.log('🔍 DEBUG: Created payment row with values:', paymentRow.value);
    return paymentRow;
  }

  addPaymentRow(invoiceNumber: string = ''): void {
    console.log('🔍 DEBUG: addPaymentRow called with invoiceNumber:', invoiceNumber);
    console.log('🔍 DEBUG: canAddMorePaymentRows():', this.canAddMorePaymentRows());
    
    if (!this.canAddMorePaymentRows()) {
      console.log('🔍 DEBUG: Cannot add more payment rows, returning early');
      return;
    }

    console.log('🔍 DEBUG: Creating payment row with invoiceNumber:', invoiceNumber);
    const control = this.createPaymentRow(invoiceNumber);
    console.log('🔍 DEBUG: Created payment row control:', control.value);
    
    console.log('🔍 DEBUG: Patching invoice balance...');
    this.patchInvoiceBalance(control);
    console.log('🔍 DEBUG: Payment row after patching balance:', control.value);
    
    console.log('🔍 DEBUG: Adding payment row to form array...');
    this.paymentRows.push(control);
    console.log('🔍 DEBUG: Payment rows count after push:', this.paymentRows.length);
    console.log('🔍 DEBUG: All payment rows:', this.paymentRows.controls.map(ctrl => ctrl.value));
  }

  removePaymentRow(index: number): void {
    if (this.paymentRows.length > 1) {
      this.paymentRows.removeAt(index);
      this.clearAvailableInvoicesCache();
      this.updateAllocationStatus();
    }
  }

  newPaymentRow(): void {
    // Create a new payment row with default values
    const newRow = this.createPaymentRow();

    // Get the next available invoice for this row
    const nextAvailableInvoice = this.getNextAvailableInvoice();

    // Set default values for the new row
    newRow.patchValue({
      linkedInvoice: nextAvailableInvoice ? nextAvailableInvoice.invoiceId : '',
      allocatedAmount: 0,
      balance: nextAvailableInvoice ? nextAvailableInvoice.balance : 0
    });

    this.paymentRows.push(newRow);
    this.clearAvailableInvoicesCache();
    this.updateAllocationStatus();
  }

  // Get the next available invoice that hasn't been selected in other rows
  private getNextAvailableInvoice(): any {
    const selectedIds = this.selectedInvoiceIds;
    return this.unpaidInvoices.find(invoice => !selectedIds.includes(invoice.invoiceId));
  }

  /* submitPayment(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const result = {
      paymentDate: this.form.get('paymentDate')?.value,
      amountRecieved: Number(this.form.get('amountRecieved')?.value || 0),
      paymentMethod: this.form.get('paymentMethod')?.value,
      notes: this.form.get('notes')?.value,
      unpaidInvoices: this.dueInvoices,
      payments: this.paymentRows.controls.map(ctrl => {
        const invoiceId = ctrl.get('linkedInvoice')?.value;
        const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === invoiceId);
        return {
          linkedInvoice: invoiceId,
          allocatedAmount: Number(ctrl.get('allocatedAmount')?.value || 0),
          balance: Number(invoice?.balance || 0)
        };
      })
    };

    this.dialogRef.close(result);
  } */

  // New method to submit payment using the new service
  submitNewPayment(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.form.disable({ emitEvent: false });

    console.log('Starting payment submission...');
    console.log('Form data:', this.form.value);

    // Validate that we have at least one allocation
    const allocations: any[] = [];
    console.log('🔍 DEBUG: Processing payment rows for submission...');
    console.log('🔍 DEBUG: Payment rows count:', this.paymentRows.length);
    console.log('🔍 DEBUG: All payment rows:', this.paymentRows.controls.map(ctrl => ctrl.value));
    
    this.paymentRows.controls.forEach((ctrl, index) => {
      const selectedInvoiceIds = ctrl.get('linkedInvoice')?.value || '';
      const allocatedAmount = Number(ctrl.get('allocatedAmount')?.value || 0);

      console.log(`🔍 DEBUG: Processing payment row ${index}:`, { selectedInvoiceIds, allocatedAmount });
      console.log(`🔍 DEBUG: selectedInvoiceIds type:`, typeof selectedInvoiceIds);
      console.log(`🔍 DEBUG: selectedInvoiceIds value:`, selectedInvoiceIds);

      if (Array.isArray(selectedInvoiceIds) && selectedInvoiceIds.length > 0 && allocatedAmount > 0) {
        // For multiple invoices, split the allocation equally or based on outstanding balances
        const totalOutstanding = selectedInvoiceIds.reduce((sum, invoiceId) => {
          const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === invoiceId);
          return sum + (invoice?.balance || 0);
        }, 0);

        selectedInvoiceIds.forEach(invoiceId => {
          console.log(`🔍 DEBUG: Processing invoice ID: ${invoiceId}`);
          const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === invoiceId);
          console.log(`🔍 DEBUG: Found invoice:`, invoice);
          const invoiceBalance = invoice?.balance || 0;

          // Calculate proportional allocation based on outstanding balance
          const proportionalAmount = totalOutstanding > 0
            ? (allocatedAmount * invoiceBalance) / totalOutstanding
            : allocatedAmount / selectedInvoiceIds.length;

          const allocation = {
            billingDocumentId: invoiceId,
            providerId: invoice?.providerId || invoice?.Provider?.id,
            amount: Math.round(proportionalAmount * 100) / 100 // Round to 2 decimal places
          };
          
          console.log(`🔍 DEBUG: Creating allocation:`, allocation);
          allocations.push(allocation);
        });
      } else if (selectedInvoiceIds && allocatedAmount > 0) {
        // Single invoice selection (backward compatibility)
        console.log(`🔍 DEBUG: Single invoice selection - ID: ${selectedInvoiceIds}, Amount: ${allocatedAmount}`);
        const allocation = {
          billingDocumentId: selectedInvoiceIds,
          amount: allocatedAmount
        };
        console.log(`🔍 DEBUG: Creating single allocation:`, allocation);
        allocations.push(allocation);
      } else {
        console.log(`🔍 DEBUG: Skipping payment row ${index} - no valid selection or amount`);
      }
    });

    console.log('Calculated allocations:', allocations);

    if (allocations.length === 0) {
      this.dialogRef.close({
        success: false,
        error: 'Please allocate payment to at least one invoice'
      });
      return;
    }

    const paymentData = {
      patientId: this.data?.patientId,
      groupId: this.data?.groupId,
      paymentDate: this.form.get('paymentDate')?.value ? new Date(this.form.get('paymentDate')?.value) : new Date(),
      amountReceived: Number(this.form.get('amountRecieved')?.value || 0),
      paymentMethod: this.form.get('paymentMethod')?.value,
      notes: this.form.get('notes')?.value,
      allocations: allocations
    };

    console.log('Payment data to send:', paymentData);

    // First, create the payment record using the new service
    this.patientFinancialService.createPayment(paymentData).subscribe({
      next: (result: any) => {
        console.log('Payment created successfully:', result);
        this.usage.flow('payment', 'done');
        console.log('About to call generatePaymentReceipts...');
        this.newPaymentData = result;

        // Extract the actual payment data from the response
        const actualPaymentData = (result as any).data || result;

        // Now generate PDF receipts for each allocation
        this.generatePaymentReceipts(actualPaymentData, paymentData);
      },
      error: (error) => {
        console.error('Error capturing payment:', error);
        this.isSaving = false;
        this.form.enable({ emitEvent: false });
        this.dialogRef.close({
          success: false,
          error: error.message || 'Failed to capture payment'
        });
      }
    });
  }

  // Generate PDF receipts for the payment
  private generatePaymentReceipts(payment: any, paymentData: any): void {
    console.log('Starting receipt generation for payment:', payment);
    console.log('Payment data:', paymentData);
    console.log('Payment object keys:', Object.keys(payment));
    console.log('Payment.PatientPaymentAllocations:', payment.PatientPaymentAllocations);
    console.log('Payment.paymentAllocations:', payment.paymentAllocations);

    // Use patient and group data from the payment response
    const group = this.group || payment.Group || this.data?.group || {};
    const patient = this.patient || payment.Patient || this.data?.patient || {};

    console.log('Group data:', group);
    console.log('Patient data:', patient);

    // Check if payment has allocations - use the correct property name from backend
    const allocations = payment.PatientPaymentAllocations || payment.paymentAllocations || [];
    console.log('Found allocations:', allocations);

    if (allocations.length === 0) {
      console.error('No payment allocations found in payment:', payment);
      this.dialogRef.close({
        success: true,
        payment: payment,
        message: 'Payment captured successfully (no allocations to generate receipts for)'
      });
      return;
    }

    // Generate receipt for each allocation
    const receiptPromises = allocations.map(async (allocation: any) => {
      console.log('Processing allocation:', allocation);

      const billingDocument = allocation.PatientBillingDocument;
      if (!billingDocument) {
        console.error('No billing document found for allocation:', allocation);
        return null;
      }

      console.log('***** CREATING RECEIPT BILLING DATA START *****');
      console.log('payment', payment);
      console.log('allocation', allocation);
      console.log('billingDocument', billingDocument);
      console.log('paymentData', paymentData);
      console.log('form', this.form.value);

      // startTime, endTime, weight, height, BMI are stored in the billingDocument.PatientBillingLineItems, we need to extract the information into the correct line item for the receipt pdf
      const lineItems = billingDocument.PatientBillingLineItems;

      // Create receipt billing document data (similar to estimate→receipt flow)
      const receiptBillingData = {
        patientId: patient.id,
        groupId: payment.groupId || this.data?.groupId,
        documentType: 'receipt' as 'receipt',
        issueDate: this.form.get('paymentDate')?.value ? new Date(this.form.get('paymentDate')?.value) : new Date(),
        dueDate: this.form.get('paymentDate')?.value ? new Date(this.form.get('paymentDate')?.value) : new Date(),
        subtotal: allocation.allocatedAmount,
        vat: 0, // Receipts typically don't have VAT
        totalDue: allocation.allocatedAmount,
        discountType: 'percentage' as 'percentage' | 'fixed',
        discount: 0,
        notes: paymentData.notes || `Payment receipt for invoice ${billingDocument?.billingNumber}`,
        paymentId: payment.id, // Link receipt to payment
        providerId: billingDocument?.Provider?.id,
        originalInvoiceNumber: billingDocument?.billingNumber, // Use original invoice number for receipt numbering
        // Persist the same display flag on the receipt so re-rendering keeps the WCA layout.
        vatExclusive: billingDocument?.vatExclusive === true,
        lineItems: [
          {
            description: this.getCreditDescription(paymentData.paymentMethod || '', billingDocument?.billingNumber || ''),
            quantity: 1,
            unitPrice: allocation.allocatedAmount,
            totalAmount: allocation.allocatedAmount,
            code: 'PAYMENT',
            codeType: 'procedure',
            date: new Date(),
          }
        ]
      };

      console.log('*** Receipt billing data: ***', receiptBillingData);

      try {
        // Step 1: Create receipt billing document using new service (same as dispute form)
        console.log('Creating receipt billing document...');
        const receiptResponse = await firstValueFrom(
          this.patientFinancialService.createBillingDocument(receiptBillingData)
        );
        const receiptDocument = receiptResponse.data;
        console.log('Receipt billing document created:', receiptDocument);

        if (!receiptDocument) {
          throw new Error('Failed to create receipt billing document');
        }

        // Step 2: Generate PDF using the old service (same as dispute form)
        // Use the billing document's outstanding balance (already updated by allocation processing)
        const outstandingBalance = parseFloat(billingDocument?.outstandingBalance || 0);
        const pdfBillingData = {
          billingNumber: receiptDocument.billingNumber,
          patientName: `${patient.name} ${patient.surname}`,
          patient: patient,
          paymentDate: paymentData.paymentDate,
          paymentMethod: paymentData.paymentMethod,
          amountRecieved: allocation.allocatedAmount,
          lineItems: billingDocument?.PatientBillingLineItems?.map((item: any) => ({
            ...item,
            balance: item.totalAmount, // Map totalAmount to balance for compatibility
            tariffCode: item.code, // Map code to tariffCode for compatibility
            tariffDescription: item.description, // Map description to tariffDescription for compatibility
            startTime: item.startTime,
            endTime: item.endTime,
            weight: item.weight,
            height: item.height,
            BMI: item.BMI
          })) || [],
          payments: [
            {
              linkedInvoice: billingDocument?.billingNumber,
              allocatedAmount: allocation.allocatedAmount,
              balance: outstandingBalance // Use the updated outstanding balance from billing document
            }
          ],
          outstandingBalance: outstandingBalance, // Pass outstanding balance for PDF generation
          notes: receiptBillingData.notes,
          practiceInfo: {
            practiceName: group.companyName || group.name,
            practiceNumber: group.practiceNumber,
            email: group.practiceEmail,
            tel: group.practicePhone
          },
          user: payment.User || payment.user || { name: 'System', surname: 'User', title: 'Dr' },
          group: group,
          provider: receiptDocument?.Provider,
          hospital: billingDocument?.priceGroupId, // Use priceGroupId instead of hospitalId
          // Receipts inherit the underlying invoice's display style — a WCA invoice produces a WCA receipt.
          vatExclusive: billingDocument?.vatExclusive === true,
          // Likewise the rate: the receipt must reflect the VAT that was actually invoiced.
          vatRate: resolveVatRate(billingDocument?.vatRate, group),
        };

        console.log('*** PDF Receipt data: ***', pdfBillingData);

        // Step 3: Generate PDF using the receipt-specific method (same as dispute form)
        console.log('Generating PDF...');
        const pdfBytes = await this.patientBillingService.generateReceiptPDF(payment.id, pdfBillingData);
        console.log('PDF generated, size:', pdfBytes.length);

        // Step 4: Convert PDF bytes to blob (same as dispute form)
        const pdfBlob = new Blob([pdfBytes as any], { type: 'application/pdf' });
        const fileName = `Receipt-${patient.name}-${patient.surname}_${Date.now()}.pdf`;

        console.log('Uploading PDF to Azure...');

        // Step 5: Upload PDF to new service (same as dispute form)
        const updatedReceiptDocument = await firstValueFrom(
          this.patientFinancialService.uploadBillingDocumentPDF(
            receiptDocument.id,
            payment.groupId || this.data?.groupId,
            pdfBlob,
            fileName
          )
        );

        console.log('Receipt PDF uploaded successfully:', updatedReceiptDocument);

        // Step 6: Trigger platform-aware download
        void this.fileDownloadService.download(pdfBlob, fileName);

        return updatedReceiptDocument;
      } catch (error) {
        console.error('Error generating receipt for allocation:', allocation, error);
        throw error;
      }
    });

    // Wait for all receipts to be generated
    Promise.all(receiptPromises)
      .then((receiptDocuments) => {
        const validReceipts = receiptDocuments.filter(doc => doc !== null);
        console.log('All receipts generated successfully:', validReceipts);
        this.dialogRef.close({
          success: true,
          payment: payment,
          receipts: validReceipts,
          message: `Payment captured successfully and ${validReceipts.length} receipts generated`
        });
      })
      .catch((error) => {
        console.error('Error generating receipts:', error);
        // Still close with success since payment was captured, but log the receipt error
        this.dialogRef.close({
          success: true,
          payment: payment,
          message: 'Payment captured successfully (receipt generation had issues)',
          error: error.message
        });
      })
      .finally(() => {
        this.isSaving = false;
        this.form.enable({ emitEvent: false });
      });
  }

  get selectedInvoiceIds(): string[] {
    const allSelectedIds: string[] = [];
    this.paymentRows.controls.forEach(ctrl => {
      const selectedIds = ctrl.get('linkedInvoice')?.value || '';
      if (Array.isArray(selectedIds)) {
        allSelectedIds.push(...selectedIds);
      } else if (selectedIds) {
        allSelectedIds.push(selectedIds);
      }
    });
    return allSelectedIds;
  }

  getAvailableInvoices(currentIndex: number): any[] {
    console.log('🔍 DEBUG: getAvailableInvoices called for index:', currentIndex);
    
    const allSelectedIds = this.selectedInvoiceIds;
    const currentRowSelectedIds = this.paymentRows.controls[currentIndex]?.get('linkedInvoice')?.value || '';
    
    console.log('🔍 DEBUG: allSelectedIds:', allSelectedIds);
    console.log('🔍 DEBUG: currentRowSelectedIds:', currentRowSelectedIds);
    console.log('🔍 DEBUG: unpaidInvoices count:', this.unpaidInvoices.length);

    // Check if we need to recalculate (if selected IDs have changed)
    const currentSelectedIdsString = allSelectedIds.sort().join(',');
    if (this.lastSelectedIds.join(',') === currentSelectedIdsString && this.availableInvoicesCache.has(currentIndex)) {
      const cached = this.availableInvoicesCache.get(currentIndex) || [];
      console.log('🔍 DEBUG: Returning cached available invoices:', cached);
      return cached;
    }

    // Prevent endless rendering by checking if we're already processing
    if (this.isProcessingAvailableInvoices) {
      console.log('🔍 DEBUG: Already processing available invoices, returning empty array');
      return [];
    }
    
    this.isProcessingAvailableInvoices = true;

    // Get IDs selected in other rows (excluding current row)
    const otherRowsSelectedIds = allSelectedIds.filter(id =>
      !currentRowSelectedIds.includes(id)
    );
    
    console.log('🔍 DEBUG: otherRowsSelectedIds:', otherRowsSelectedIds);

    const availableInvoices = this.unpaidInvoices.filter((inv) => {
      return !otherRowsSelectedIds.includes(inv.invoiceId);
    });
    
    console.log('🔍 DEBUG: Available invoices for index', currentIndex, ':', availableInvoices);

    // Cache the result
    this.availableInvoicesCache.set(currentIndex, availableInvoices);
    this.lastSelectedIds = [...allSelectedIds];
    
    // Reset the processing flag
    this.isProcessingAvailableInvoices = false;

    return availableInvoices;
  }

  // Helper method to get display text for selected invoices
  getSelectedInvoicesDisplay(selectedIds: string[]): string {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return 'Select invoices...';
    }

    if (selectedIds.length === 1) {
      const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === selectedIds[0]);
      return `#${invoice?.billingNumber || selectedIds[0]} (R${invoice?.balance || 0})`;
    }

    const totalBalance = selectedIds.reduce((sum, id) => {
      const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === id);
      return sum + (invoice?.balance || 0);
    }, 0);

    return `${selectedIds.length} invoices selected (Total: R${totalBalance})`;
  }

  // Helper method to show allocation preview
  getAllocationPreview(selectedIds: string[], allocatedAmount: number): string {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0 || allocatedAmount <= 0) {
      return '';
    }

    if (selectedIds.length === 1) {
      return `Full amount (R${allocatedAmount}) will be allocated to this invoice`;
    }

    const totalOutstanding = selectedIds.reduce((sum, id) => {
      const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === id);
      return sum + (invoice?.balance || 0);
    }, 0);

    if (totalOutstanding === 0) {
      return `Amount will be split equally (R${(allocatedAmount / selectedIds.length).toFixed(2)} each)`;
    }

    const previews = selectedIds.map(id => {
      const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === id);
      const invoiceBalance = invoice?.balance || 0;
      const proportionalAmount = (allocatedAmount * invoiceBalance) / totalOutstanding;
      return `#${invoice?.billingNumber || id}: R${proportionalAmount.toFixed(2)}`;
    });

    return `Proportional allocation: ${previews.join(', ')}`;
  }

  // Get total allocated amount across all payment rows
  getTotalAllocatedAmount(): number {
    return this.paymentRows.controls.reduce((total, row) => {
      return total + Number(row.get('allocatedAmount')?.value || 0);
    }, 0);
  }

  // Get remaining amount to allocate
  getRemainingAmountToAllocate(): number {
    const totalReceived = this.getNumberValue('amountRecieved');
    const totalAllocated = this.getTotalAllocatedAmount();
    return Math.max(0, totalReceived - totalAllocated);
  }

  canAddMorePaymentRows(): boolean {
    // Allow adding new rows if there are unpaid invoices available
    // or if there's remaining amount to allocate
    const remainingAmount = this.getRemainingAmountToAllocate();
    const availableInvoices = this.unpaidInvoices.length;
    const selectedIds = this.selectedInvoiceIds;

    // Can add if there's remaining amount and either:
    // 1. There are more invoices than currently selected, OR
    // 2. There's remaining amount to allocate (even if all invoices are selected)
    return remainingAmount > 0 && (availableInvoices > selectedIds.length || remainingAmount > 0);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  patchInvoiceBalance(control: AbstractControl) {
    console.log('🔍 DEBUG: patchInvoiceBalance called for control:', control.value);
    console.log('🔍 DEBUG: Current linkedInvoice value:', control.get('linkedInvoice')?.value);
    
    control.get('linkedInvoice')?.valueChanges
      .pipe(startWith(control.get('linkedInvoice')?.value))
      .subscribe((selectedInvoiceIds) => {
        console.log('🔍 DEBUG: linkedInvoice value changed to:', selectedInvoiceIds);
        
        if (Array.isArray(selectedInvoiceIds)) {
          console.log('🔍 DEBUG: Processing array of selected invoice IDs');
          // Calculate total balance for all selected invoices
          const totalBalance = selectedInvoiceIds.reduce((sum, invoiceId) => {
            const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === invoiceId);
            console.log('🔍 DEBUG: Found invoice for ID', invoiceId, ':', invoice);
            return sum + (invoice?.balance || 0);
          }, 0);
          console.log('🔍 DEBUG: Setting balance to:', totalBalance);
          control.get('balance')?.setValue(totalBalance);
        } else if (selectedInvoiceIds) {
          console.log('🔍 DEBUG: Processing single selected invoice ID:', selectedInvoiceIds);
          // Single invoice selection (backward compatibility)
          const invoice = this.unpaidInvoices.find(inv => inv.invoiceId === selectedInvoiceIds);
          console.log('🔍 DEBUG: Found invoice for single ID:', invoice);
          if (invoice) {
            console.log('🔍 DEBUG: Setting balance to:', invoice.balance || 0);
            control.get('balance')?.setValue(invoice.balance || 0);
          }
        } else {
          console.log('🔍 DEBUG: No selected invoice IDs, setting balance to 0');
          control.get('balance')?.setValue(0);
        }
      });
  }

  private updateAllocationStatus(): void {
    const received = this.getNumberValue('amountRecieved');
    const totalAllocated = this.getTotalAllocatedAmount();
    this.remainingToAllocate = +(received - totalAllocated).toFixed(2);
  }

  get disablePayment(): boolean {
    const received = this.getNumberValue('amountRecieved');
    const totalAllocated = this.getTotalAllocatedAmount();
    const remainingToAllocate = this.getRemainingAmountToAllocate();
    const invoiceIds = new Set<string>();

    // Check if form is invalid
    if (this.form.invalid || isNaN(received) || received <= 0) {
      return true;
    }

    // Check for over-allocation
    if (remainingToAllocate < 0) {
      return true;
    }

    // Check each payment row
    for (const paymentRow of this.paymentRows.controls) {
      const selectedInvoiceIds = paymentRow.get('linkedInvoice')?.value || '';
      const allocated = Number(paymentRow.get('allocatedAmount')?.value || 0);

      // Check for duplicate selections across all rows
      if (Array.isArray(selectedInvoiceIds)) {
        for (const invoiceId of selectedInvoiceIds) {
          if (invoiceIds.has(invoiceId)) return true; // Prevent duplicate allocations to same invoice
          invoiceIds.add(invoiceId);
        }
      } else if (selectedInvoiceIds) {
        if (invoiceIds.has(selectedInvoiceIds)) return true;
        invoiceIds.add(selectedInvoiceIds);
      }

      // Validate allocation amount
      if (allocated <= 0) return true;

      // Check if allocation exceeds total received amount
      if (allocated > received) return true;
    }

    // Ensure at least one payment row has allocations
    const hasAllocations = this.paymentRows.controls.some(row => {
      const selectedIds = row.get('linkedInvoice')?.value || '';
      const allocated = Number(row.get('allocatedAmount')?.value || 0);
      return (Array.isArray(selectedIds) ? selectedIds.length > 0 : selectedIds) && allocated > 0;
    });

    return !hasAllocations;
  }

  getNumberValue(controlName: string): number {
    const value = this.form.get(controlName)?.value;
    return Number(value || 0);
  }

  autoAllocatePayment(): void {
    console.log('Auto-allocating payment...');
    const amountReceived = this.getNumberValue('amountRecieved');
    console.log('Amount received:', amountReceived);
    console.log('Unpaid invoices:', this.unpaidInvoices);
    if (amountReceived <= 0 || this.unpaidInvoices.length === 0) {
      return;
    }

    // Sort invoices by date (most recent first) - assuming invoices have a date property
    // For now, we'll use the invoice ID as a proxy for date (assuming newer invoices have higher IDs)
    const sortedInvoices = [...this.unpaidInvoices].sort((a, b) => {
      // Try to extract numeric part from invoice ID for sorting
      const aNum = parseInt(a.invoiceId.replace(/\D/g, '')) || 0;
      const bNum = parseInt(b.invoiceId.replace(/\D/g, '')) || 0;
      return bNum - aNum; // Most recent first
    });

    console.log('Sorted invoices:', sortedInvoices);

    let remainingAmount = amountReceived;
    const allocations: { invoiceId: string; amount: number }[] = [];

    // Allocate funds starting from most recent invoice
    for (const invoice of sortedInvoices) {
      if (remainingAmount <= 0) break;

      const invoiceBalance = invoice.balance || 0;
      const allocationAmount = Math.min(remainingAmount, invoiceBalance);

      if (allocationAmount > 0) {
        allocations.push({
          invoiceId: invoice.invoiceId,
          amount: allocationAmount
        });
        remainingAmount -= allocationAmount;
      }
    }

    console.log('Allocations:', allocations);

    // Update the form with allocations
    this.updatePaymentAllocations(allocations);
  }

  private updatePaymentAllocations(allocations: { invoiceId: string; amount: number }[]): void {
    console.log('Updating payment allocations...');

    // Clear the cache since we're changing form values
    this.clearAvailableInvoicesCache();

    // Clear existing payment rows
    while (this.paymentRows.length > 0) {
      this.paymentRows.removeAt(0);
    }

    // Add new payment rows with patched values
    allocations.forEach(allocation => {
      console.log('Creating payment row for invoice:', allocation.invoiceId);
      const paymentRow = this.createPaymentRow(allocation.invoiceId);
      paymentRow.patchValue({
        linkedInvoice: allocation.invoiceId, // Patch the select value
        allocatedAmount: allocation.amount
      });
      this.paymentRows.push(paymentRow);
    });

    // If there are no allocations but we have unpaid invoices, add at least one empty payment row
    if (allocations.length === 0 && this.unpaidInvoices.length > 0) {
      this.addPaymentRow();
    }

    // Ensure the UI reflects the allocation
    this.paymentRows.controls.forEach((row, idx) => {
      console.log('Updating payment row:', row);
      console.log('Allocation:', allocations[idx]);
      if (allocations[idx]) {
        row.get('linkedInvoice')?.setValue(allocations[idx].invoiceId);
        row.get('allocatedAmount')?.setValue(allocations[idx].amount);
        console.log('Updated payment row:', row);
      }
    });

    this.updateAllocationStatus();
  }

  // Method to clear the available invoices cache
  private clearAvailableInvoicesCache(): void {
    this.availableInvoicesCache.clear();
    this.lastSelectedIds = [];
  }

  // Method to force refresh available invoices (useful for debugging)
  public refreshAvailableInvoices(): void {
    this.clearAvailableInvoicesCache();
  }

  // TrackBy function for better performance in ngFor
  public trackByInvoiceId(index: number, invoice: any): string {
    return invoice.invoiceId;
  }



  // Calculate excess credit (amount received minus total unpaid invoices)
  get excessCredit(): number {
    const totalUnpaid = this.unpaidInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0);
    const received = this.getNumberValue('amountRecieved');
    return Math.max(0, received - totalUnpaid);
  }


}
